import { AxiosResponse } from 'axios';
import { NextApiRequestQuery } from 'next/dist/server/api-utils';
import {
    FunctionaryInterface,
    GoalInterface,
    MatchCreateUpdateApiInterface,
    MatchesConfrimRecordApiInterface,
    MatchFunctionaryApiInterface,
    MatchPlayerApiInterface,
    MatchPlayerGoalApiInterface,
    MatchPlayerMoveApiInterface,
    MatchPlayerPunishmentApiInterface,
    MatchPlayerRotationApiInterface,
    MatchPlayersRecordInterface,
    MatchRecordUpdateApiInterface,
    MatchRefereeSetApiInterface,
    PunishmentInterface,
    MatchCreateDelegateReportApiInterface,
    MatchDelegateReportApiInterface,
    MatchSaveDelegateReportRankingsApiInterface,
    MatchUpdateRewardsInterface,
    RefereeApiEnum,
    MatchRewardApiInterface,
    MatchCreateEditRefereeReportApiInterface,
    MatchRefereeReportApiInterface,
    TeamSideEnum,
} from 'submodules/api_middleware';
import { RotationResponseInterface } from 'components/interfaces/MatchInterface';
import { ApiResponseInterface } from 'utils/helpers/browser/getResponse';
import fetcher, {
    fetcherFull,
    MethodEnum,
} from 'utils/helpers/browser/newFetcher';

interface ApiResponseRotation extends ApiResponseInterface {
    data: RotationResponseInterface;
}

interface ApiResponseGoal extends ApiResponseInterface {
    data: GoalInterface;
}

export interface ApiResponsePunishment extends ApiResponseInterface {
    data: PunishmentInterface;
}

export interface ApiResponseReport extends ApiResponseInterface {
    data: MatchRefereeReportApiInterface;
}

export interface ApiResponseDelegateReport extends ApiResponseInterface {
    data: MatchDelegateReportApiInterface;
}

interface ApiResponseFunctionary extends ApiResponseInterface {
    data: FunctionaryInterface;
}

export interface ApiResponsePlayer extends ApiResponseInterface {
    data: MatchPlayersRecordInterface;
}

export interface ApiResponseReward extends ApiResponseInterface {
    data: MatchRewardApiInterface[];
}

export class MatchService {
    getList = async (
        query: NextApiRequestQuery
    ): Promise<ApiResponseInterface> => {
        return await fetcher(`/api/matches/list`, MethodEnum.GET, query);
    };

    getListFull = async (
        query: NextApiRequestQuery
    ): Promise<AxiosResponse> => {
        return await fetcherFull(`/api/matches/list`, MethodEnum.GET, query);
    };

    async editRecord(
        matchId: number,
        data: MatchRecordUpdateApiInterface
    ): Promise<ApiResponseInterface> {
        return await fetcher<ApiResponseInterface>(
            `/api/matches/${matchId}/record/edit`,
            MethodEnum.PUT,
            null,
            { matchId, data }
        );
    }

    async create(
        data: MatchCreateUpdateApiInterface
    ): Promise<ApiResponseInterface> {
        return await fetcher<ApiResponseInterface>(
            '/api/matches/create',
            MethodEnum.POST,
            null,
            { data }
        );
    }

    async edit(
        matchId: number,
        data: MatchCreateUpdateApiInterface
    ): Promise<ApiResponseInterface> {
        return await fetcher<ApiResponseInterface>(
            `/api/matches/${matchId}/edit`,
            MethodEnum.PUT,
            null,
            { matchId, data }
        );
    }

    async delete(matchId: number): Promise<ApiResponseInterface> {
        return await fetcher<ApiResponseInterface>(
            `/api/matches/${matchId}/delete`,
            MethodEnum.DELETE,
            null,
            { matchId }
        );
    }

    async end(matchId: number): Promise<ApiResponseInterface> {
        return await fetcher<ApiResponseInterface>(
            `/api/matches/${matchId}/record/end`,
            MethodEnum.POST,
            null,
            { matchId }
        );
    }

    async confirm(
        matchId: number,
        teamSide: TeamSideEnum,
        data: MatchesConfrimRecordApiInterface
    ): Promise<ApiResponseInterface> {
        return await fetcher<ApiResponseInterface>(
            `/api/matches/${matchId}/record/confirm`,
            MethodEnum.POST,
            null,
            { matchId, teamSide, data }
        );
    }

    async close(matchId: number): Promise<ApiResponseInterface> {
        return await fetcher<ApiResponseInterface>(
            `/api/matches/${matchId}/record/close`,
            MethodEnum.POST,
            null,
            { matchId }
        );
    }

    async cancel(matchId: number): Promise<ApiResponseInterface> {
        return await fetcher<ApiResponseInterface>(
            `/api/matches/${matchId}/record/cancel`,
            MethodEnum.POST,
            null,
            { matchId }
        );
    }

    async addReportFile(
        matchId: number,
        reportId: number,
        document: File
    ): Promise<ApiResponseReport> {
        const formData = new FormData();
        formData.append('document', document);

        return await fetcherFull<ApiResponseReport>(
            `/api/matches/${matchId}/report/${reportId}/referee/file/create`,
            MethodEnum.POST,
            null,
            formData
        );
    }

    async removeReportFile(
        matchId: number,
        reportId: number,
        fileId: number
    ): Promise<ApiResponseReport> {
        return await fetcher<ApiResponseReport>(
            `/api/matches/${matchId}/report/${reportId}/referee/file/delete/${fileId}`,
            MethodEnum.DELETE
        );
    }

    async addReport(
        matchId: number,
        report: MatchCreateEditRefereeReportApiInterface
    ): Promise<ApiResponseReport> {
        return await fetcher<ApiResponseReport>(
            `/api/matches/${matchId}/report/create`,
            MethodEnum.POST,
            null,
            { matchId, report }
        );
    }

    async editReport(
        matchId: number,
        reportId: number,
        report: MatchCreateEditRefereeReportApiInterface
    ): Promise<ApiResponseReport> {
        return await fetcher<ApiResponseReport>(
            `/api/matches/${matchId}/report/edit`,
            MethodEnum.PUT,
            null,
            { matchId, reportId, report }
        );
    }

    async removeReport(
        matchId: number,
        reportId: number
    ): Promise<ApiResponseReport> {
        return await fetcher<ApiResponseReport>(
            `/api/matches/${matchId}/report/delete`,
            MethodEnum.DELETE,
            null,
            { matchId, reportId }
        );
    }

    async createDelegateReport(
        matchId: number,
        delegateReport: MatchCreateDelegateReportApiInterface
    ): Promise<ApiResponseReport> {
        return await fetcher<ApiResponseReport>(
            `/api/matches/${matchId}/report/delegate/create`,
            MethodEnum.POST,
            null,
            { matchId, data: delegateReport }
        );
    }

    async finishDelegateReport(matchId: number): Promise<ApiResponseReport> {
        return await fetcher<ApiResponseReport>(
            `/api/matches/${matchId}/report/delegate/finish`,
            MethodEnum.POST
        );
    }

    async saveDelegateReportRanking(
        matchId: number,
        rankings: MatchSaveDelegateReportRankingsApiInterface
    ): Promise<ApiResponseDelegateReport> {
        return await fetcher<ApiResponseDelegateReport>(
            `/api/matches/${matchId}/report/delegate/ranking/save`,
            MethodEnum.POST,
            null,
            { matchId, data: rankings }
        );
    }

    async addDelegateReportFile(
        matchId: number,
        document: File
    ): Promise<ApiResponseReport> {
        const formData = new FormData();
        formData.append('document', document);

        return await fetcherFull<ApiResponseReport>(
            `/api/matches/${matchId}/report/delegate/file/create`,
            MethodEnum.POST,
            null,
            formData
        );
    }

    async removeDelegateReportFile(
        matchId: number,
        fileId: number
    ): Promise<ApiResponseReport> {
        return await fetcher<ApiResponseReport>(
            `/api/matches/${matchId}/report/delegate/file/delete/${fileId}`,
            MethodEnum.DELETE
        );
    }

    async createRotation(
        teamSide: TeamSideEnum,
        matchId: number,
        rotation: MatchPlayerRotationApiInterface
    ): Promise<ApiResponseRotation> {
        return await fetcher<ApiResponseRotation>(
            `/api/matches/${matchId}/rotations/${teamSide}/create`,
            MethodEnum.POST,
            null,
            { matchId, teamSide, rotation }
        );
    }

    async deleteRotation(
        teamSide: TeamSideEnum,
        matchId: number,
        rotationId: number
    ): Promise<ApiResponseRotation> {
        return await fetcher<ApiResponseRotation>(
            `/api/matches/${matchId}/rotations/${teamSide}/delete`,
            MethodEnum.DELETE,
            null,
            { matchId, rotationId, teamSide }
        );
    }

    async createGoal(
        teamSide: TeamSideEnum,
        matchId: number,
        goal: MatchPlayerGoalApiInterface
    ): Promise<ApiResponseGoal> {
        return await fetcher<ApiResponseGoal>(
            `/api/matches/${matchId}/goals/${teamSide}/create`,
            MethodEnum.POST,
            null,
            { matchId, teamSide, goal }
        );
    }

    async editGoal(
        teamSide: TeamSideEnum,
        matchId: number,
        goalId: number,
        data: MatchPlayerGoalApiInterface
    ): Promise<ApiResponseGoal> {
        return await fetcher<ApiResponseGoal>(
            `/api/matches/${matchId}/goals/${teamSide}/edit`,
            MethodEnum.PUT,
            null,
            { matchId, goalId, teamSide, data }
        );
    }

    async deleteGoal(
        teamSide: TeamSideEnum,
        matchId: number,
        goalId: number
    ): Promise<ApiResponseGoal> {
        return await fetcher<ApiResponseGoal>(
            `/api/matches/${matchId}/goals/${teamSide}/delete`,
            MethodEnum.DELETE,
            null,
            { matchId, goalId, teamSide }
        );
    }

    async createPunishment(
        teamSide: TeamSideEnum,
        matchId: number,
        punishment: MatchPlayerPunishmentApiInterface
    ): Promise<ApiResponsePunishment> {
        return await fetcher<ApiResponsePunishment>(
            `/api/matches/${matchId}/punishments/${teamSide}/create`,
            MethodEnum.POST,
            null,
            { matchId, teamSide, punishment }
        );
    }

    async editPunishment(
        teamSide: TeamSideEnum,
        matchId: number,
        punishmentId: number,
        data: MatchPlayerPunishmentApiInterface
    ): Promise<ApiResponsePunishment> {
        return await fetcher<ApiResponsePunishment>(
            `/api/matches/${matchId}/punishments/${teamSide}/edit`,
            MethodEnum.PUT,
            null,
            { matchId, punishmentId, teamSide, data }
        );
    }

    async deletePunishment(
        teamSide: TeamSideEnum,
        matchId: number,
        punishmentId: number
    ): Promise<ApiResponsePunishment> {
        return await fetcher<ApiResponsePunishment>(
            `/api/matches/${matchId}/punishments/${teamSide}/delete`,
            MethodEnum.DELETE,
            null,
            { matchId, punishmentId, teamSide }
        );
    }

    async createFunctionary(
        teamSide: TeamSideEnum,
        matchId: number,
        functionary: MatchFunctionaryApiInterface
    ): Promise<ApiResponseFunctionary> {
        return await fetcher<ApiResponseFunctionary>(
            `/api/matches/${matchId}/persons/${teamSide}/create`,
            MethodEnum.POST,
            null,
            { matchId, teamSide, functionary }
        );
    }

    async functionarySearchList(
        teamSide: TeamSideEnum,
        matchId: number,
        query: NextApiRequestQuery
    ): Promise<AxiosResponse> {
        return await fetcherFull(
            `/api/matches/${matchId}/persons/${teamSide}/search`,
            MethodEnum.GET,
            query
        );
    }

    async playersSearchList(
        teamSide: TeamSideEnum,
        matchId: number,
        query: NextApiRequestQuery
    ): Promise<AxiosResponse> {
        return await fetcherFull(
            `/api/matches/${matchId}/players/${teamSide}/search`,
            MethodEnum.GET,
            query
        );
    }

    async deleteFunctionary(
        teamSide: TeamSideEnum,
        matchId: number,
        functionaryId: number
    ): Promise<ApiResponseFunctionary> {
        return await fetcher<ApiResponseFunctionary>(
            `/api/matches/${matchId}/persons/${teamSide}/delete`,
            MethodEnum.DELETE,
            null,
            { matchId, functionaryId, teamSide }
        );
    }

    async createPlayer(
        teamSide: TeamSideEnum,
        matchId: number,
        player: MatchPlayerApiInterface
    ): Promise<ApiResponsePlayer> {
        return await fetcher<ApiResponsePlayer>(
            `/api/matches/${matchId}/players/${teamSide}/create`,
            MethodEnum.POST,
            null,
            { matchId, teamSide, player }
        );
    }

    async editPlayer(
        teamSide: TeamSideEnum,
        matchId: number,
        person_id: number,
        player: MatchPlayerApiInterface
    ): Promise<ApiResponsePlayer> {
        return await fetcher<ApiResponsePlayer>(
            `/api/matches/${matchId}/players/${teamSide}/edit`,
            MethodEnum.PUT,
            null,
            { matchId, teamSide, person_id, player }
        );
    }

    async deletePlayer(
        teamSide: TeamSideEnum,
        matchId: number,
        playerId: number
    ): Promise<ApiResponseFunctionary> {
        return await fetcher<ApiResponseFunctionary>(
            `/api/matches/${matchId}/players/${teamSide}/delete`,
            MethodEnum.DELETE,
            null,
            { matchId, playerId, teamSide }
        );
    }

    async movePlayer(
        teamSide: TeamSideEnum,
        matchId: number,
        data: MatchPlayerMoveApiInterface
    ): Promise<ApiResponseFunctionary> {
        return await fetcher<ApiResponseFunctionary>(
            `/api/matches/${matchId}/players/${teamSide}/move`,
            MethodEnum.PUT,
            null,
            { matchId, teamSide, data }
        );
    }

    async copyPlayersAndPersonsFromPreviousMatch(
        teamSide: TeamSideEnum,
        matchId: number
    ): Promise<ApiResponseFunctionary> {
        return await fetcher<ApiResponseFunctionary>(
            `/api/matches/${matchId}/${teamSide}/copy_players_and_persons_from_prev_match`,
            MethodEnum.POST
        );
    }

    async createReward(
        matchId: number,
        data: MatchUpdateRewardsInterface
    ): Promise<ApiResponseReward> {
        return await fetcher<ApiResponseReward>(
            `/api/matches/${matchId}/rewards/create`,
            MethodEnum.POST,
            null,
            { matchId, data }
        );
    }

    async editReferee(
        matchId: number,
        referee: MatchRefereeSetApiInterface
    ): Promise<ApiResponseReward> {
        return await fetcher<ApiResponseReward>(
            `/api/matches/${matchId}/referees/edit`,
            MethodEnum.POST,
            null,
            { matchId, referee }
        );
    }

    async getDelegation(
        matchId: number,
        role: RefereeApiEnum,
        query: any
    ): Promise<AxiosResponse> {
        return await fetcherFull(
            `/api/matches/${matchId}/delegation/${role}`,
            MethodEnum.GET,
            query
        );
    }

    async check(matchId: number): Promise<ApiResponseInterface> {
        return await fetcher<AxiosResponse<string[]>>(
            `/api/matches/${matchId}/check`,
            MethodEnum.GET
        );
    }

    async generateTestMatch(
        state: string,
        minutes_to_next_state: number
    ): Promise<AxiosResponse> {
        return await fetcher(
            `/api/matches/example-generate/${state}`,
            MethodEnum.POST,
            null,
            { minutes_to_next_state }
        );
    }
}
